import {
	useCommandPaletteActions,
	CommandPaletteActions
} from '@/command-palette/use-command-palette-actions';
import { capitalize } from '@/utils';
import { useRoute, useRouter } from 'vue-router';
import {
	useConversations,
	useMessages
} from '../composition';
import { useMoveMessage } from './use-move-message';

let isListening = false;

export function useConversationsActions() {
	const {
		selectedConversation,
		selectedConversationsSize,
		markSelectedConversationsDone,
		markSelectedConversationsOpened,
		markSelectedConversationsAsRead,
		markAsOpened,
		markAsClosed,
		markConversationAsRead,
		activeFilterBy,
		loadConversations
	} = useConversations();
	const {
		selectedMessage,
		selectedMessages,
		markSelectedMessagesAsRead,
		markSelectedMessagesAsUnread,
		deleteSelectedMessages,
		setMessageRead,
		setMessageUnread,
		deleteMessage
	} = useMessages();
	const {
		setActions,
		commandPalleteBus,
		setLoadingMessage,
		setIsLoading,
		setIsOpened
	} = useCommandPaletteActions();
	const { openDialog } = useMoveMessage();

	const route = useRoute();
	const router = useRouter();

	function initListening() {
		commandPalleteBus.on(async (params) => {
			switch (params.event) {
				case 'mark_selected_done': {
					setLoadingMessage(
						'Marking selected conversations as done...'
					);
					setIsLoading(true);
					await markSelectedConversationsDone();
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'mark_selected_opened': {
					setLoadingMessage(
						'Marking selected conversations as undone...'
					);
					setIsLoading(true);
					await markSelectedConversationsOpened();
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'mark_selected_read': {
					setLoadingMessage(
						'Marking selected conversations as read...'
					);
					setIsLoading(true);
					await markSelectedConversationsAsRead();
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'mark_selected_messages_read': {
					setLoadingMessage(
						'Marking selected messages as read...'
					);
					setIsLoading(true);
					await markSelectedMessagesAsRead(
						selectedConversation.value!.id
					);
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'mark_selected_messages_unread': {
					setLoadingMessage(
						'Marking selected messages as unread...'
					);
					setIsLoading(true);
					await markSelectedMessagesAsUnread(
						selectedConversation.value!.id
					);
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'delete_selected_message': {
					setLoadingMessage(
						'Deleting selected messages...'
					);
					setIsLoading(true);
					await deleteSelectedMessages(
						selectedConversation.value!.id
					);
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'open_conversation': {
					if (!selectedConversation.value) {
						return;
					}
					setIsOpened(false);
					return router.push({
						name: 'ConversationDetails',
						params: {
							leadId: selectedConversation
								.value.id
						}
					});
				}
				case 'mark_message_as_unread': {
					if (!selectedMessage.value) {
						return;
					}
					setIsLoading(true);
					setLoadingMessage(
						'Marking message as unread...'
					);
					setMessageUnread(
						selectedMessage.value
							.conversation_id,
						selectedMessage.value
					);
					setLoadingMessage('');
					setIsLoading(false);

					return setIsOpened(false);
				}
				case 'mark_message_as_read': {
					if (!selectedMessage.value) {
						return;
					}
					setIsLoading(true);
					setLoadingMessage(
						'Marking message as read...'
					);
					setMessageRead(
						selectedMessage.value
							.conversation_id,
						selectedMessage.value
					);
					setLoadingMessage('');
					setIsLoading(false);
					return setIsOpened(false);
				}
				case 'toggle_done': {
					if (!selectedConversation.value) {
						return;
					}

					setIsLoading(true);
					if (
						selectedConversation.value.is_closed
					) {
						setLoadingMessage(
							'Marking conversation as opened...'
						);
						await markAsOpened(
							selectedConversation.value
						);
					} else {
						setLoadingMessage(
							'Marking conversation as closed...'
						);
						await markAsClosed(
							selectedConversation.value
						);
					}

					setIsLoading(false);
					setIsOpened(false);
					break;
				}
				case 'mark_conversation_as_read': {
					if (!selectedConversation.value) {
						return;
					}

					setIsLoading(true);
					setLoadingMessage(
						'Marking conversation as read...'
					);
					await markConversationAsRead(
						selectedConversation.value
					);
					setLoadingMessage('');
					setIsLoading(false);
					setIsOpened(false);
					return;
				}
				case 'assign_to': {
					// TODO: open assign team member dialog
					return;
				}
				case 'show_open': {
					if (activeFilterBy.value === 'open') {
						return setIsOpened(false);
					}

					loadConversations(1, 'open');
					return setIsOpened(false);
				}
				case 'show_closed': {
					if (activeFilterBy.value === 'closed') {
						return setIsOpened(false);
					}

					loadConversations(1, 'closed');
					return setIsOpened(false);
				}
				case 'move_message': {
					if (!selectedMessage.value) {
						return;
					}

					setIsOpened(false);
					openDialog(selectedMessage.value);
					break;
				}
				case 'show_unread': {
					if (activeFilterBy.value === 'unread') {
						return setIsOpened(false);
					}

					loadConversations(1, 'unread');
					return setIsOpened(false);
				}
				case 'delete_message': {
					if (!selectedMessage.value) {
						return;
					}

					setIsLoading(true);
					setLoadingMessage(
						'Removing message...'
					);
					await deleteMessage(
						selectedMessage.value
					);
					setLoadingMessage('');
					setIsLoading(false);

					return setIsOpened(false);
				}
				case 'open_message': {
					if (!selectedMessage.value) {
						return;
					}

					setIsOpened(false);
					return router.push({
						name: 'MessageDetails',
						params: {
							leadId: selectedMessage.value
								.conversation_id,
							messageUid:
								selectedMessage.value.uid
						}
					});
				}
			}
		});
	}

	initListening();

	const basicActions: CommandPaletteActions[] = [
		{
			label: 'Conversations',
			commands: [
				{
					title: 'Show Open',
					event: 'show_open'
				},
				{
					title: 'Show Closed',
					event: 'show_closed'
				},
				{
					title: 'Show Unread',
					event: 'show_unread'
				}
			]
		}
	];

	function conversationActions(isOnDetails?: boolean) {
		return [
			{
				label: 'Conversation',
				commands: [
					{
						title: selectedConversation.value!
							.is_closed
							? 'Mark as opened'
							: 'Mark as closed',
						event: 'toggle_done',
						key: selectedConversation.value!
							.is_closed
							? 'O'
							: 'R',
						eventOnKeys: [
							selectedConversation.value!
								.is_closed
								? 'O'
								: 'D'
						]
					},
					{
						title: 'Mark as read',
						key: 'R',
						event: 'mark_conversation_as_read',
						eventOnKeys: ['R']
					},
					{
						title: 'Assign to',
						key: 'A',
						event: 'assign_to'
					},
					...(isOnDetails
						? []
						: [
								{
									title: 'Open Conversation',
									key: 'Enter',
									event: 'open_conversation'
								}
						  ])
				]
			}
		];
	}

	function setBasicActions() {
		setActions('Conversations', basicActions);
	}

	function setConversationActions(isOnDetails?: boolean) {
		if (!selectedConversation.value) {
			return;
		}

		setActions(
			`Conversation with ${selectedConversation.value.lead.name}`,
			isOnDetails
				? conversationActions(isOnDetails)
				: [
						...conversationActions(isOnDetails),
						...basicActions
				  ]
		);
	}

	function setMessageActions() {
		if (!selectedMessage.value) {
			return;
		}
		const label = () => {
			if (
				selectedMessage.value!.type ===
				'text-message'
			) {
				return 'SMS';
			}

			return capitalize(
				selectedMessage.value!.type.replace(
					'_',
					' '
				)
			);
		};

		const detailsAction =
			route.name === 'MessageDetails'
				? []
				: [
						{
							title: 'Open message',
							key: 'Enter',
							event: 'open_message'
						}
				  ];

		const readAction =
			selectedMessage.value?.content.direction ===
			'incoming'
				? [
						selectedMessage.value.read_at
							? {
									title: 'Mark as unread',
									event: 'mark_message_as_unread',
									key: 'U',
									eventOnKeys: ['U']
							  }
							: {
									title: 'Mark as read',
									event: 'mark_message_as_read',
									key: 'U',
									eventOnKeys: ['U']
							  }
				  ]
				: [];

		setActions(label(), [
			{
				label: label(),
				commands: [
					...readAction,
					{
						title: 'Move',
						event: 'move_message',
						key: 'M',
						eventOnKeys: ['m']
					},
					{
						title: 'Delete',
						event: 'delete_message',
						key: '#',
						eventOnKeys: ['Shift', '#']
					},
					...detailsAction
				]
			},
			...conversationActions(true)
		]);
	}

	function setBulkActions() {
		setActions(
			`Manage ${selectedConversationsSize.value} selected conversations`,
			[
				{
					label: 'Conversations bulk actions',
					commands: [
						{
							title: 'Mark as closed',
							event: 'mark_selected_done'
						},
						{
							title: 'Mark as opened',
							event: 'mark_selected_opened'
						},
						{
							title: 'Mark as read',
							event: 'mark_selected_read'
						}
					]
				}
			]
		);
	}

	function setBulkMessageActions() {
		setActions(
			`Manage ${selectedMessages.value.size} selected messages`,
			[
				{
					label: 'Messages bulk actions',
					commands: [
						{
							title: 'Mark as read',
							event: 'mark_selected_messages_read'
						},
						{
							title: 'Mark as unread',
							event: 'mark_selected_messages_unread'
						},
						{
							title: 'Delete',
							event: 'delete_selected_message'
						}
					]
				}
			]
		);
	}

	return {
		setBasicActions,
		setConversationActions,
		setMessageActions,
		setBulkActions,
		setBulkMessageActions
	};
}
